import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BotList from './RCSBotList';
import BotDetail from './RCSBotDetail';
import CreateBot from './RCSCreateBot';
import BrandList from './RCSBrandList';
import CreateBrand from './RCSCreateBrand';
import { useAuth } from '../../hooks/AuthProvider';
import TemplateList from './RCSTemplateList';
import CreateTemplate from './RCSCreateTemplate';
import BroadcastTemplate from './RCSBroadcastTemplate';
import BotVerificationWizard from './BotVerificationWizard';
import { GET_RCS_BOTS, GET_RCS_BRAND } from "../../config/urls";
import axios from 'axios';
import {
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const RCSDashboardNew = (props) => {
  const theme = useTheme();
  // Navigation state
  const [currentView, setCurrentView] = useState(props?.pageParams?.currentView ? props?.pageParams?.currentView :'bot-list');
  const [selectedBot, setSelectedBot] = useState(props?.pageParams?.bot ? props?.pageParams?.bot : null);
  const [selectedTemplate, setSelectedTemplate] = useState(props?.pageParams?.template ? props?.pageParams?.template: null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const { getValidToken } = useAuth();
  // null until loaded, so the brand list doesn't flash its empty state
  const [brands, setBrands] = useState(null);
  // Where Create Brand goes after saving: 'bot' continues to bot creation, 'list' returns to the brand list
  const [brandReturn, setBrandReturn] = useState('bot');
  
  // Data state - initialize as empty array
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch bots on initial load and when returning to bot list
  useEffect(() => {
    if (currentView === 'bot-list') {
      fetchBots();
    } else if (['brand-list', 'create-brand'].includes(currentView)) {
      fetchBrands();
    }
  }, [currentView]);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(GET_RCS_BRAND, {
        headers: {
          Authorization: `Bearer ${await getValidToken()}`,
          'X-Tenant-ID': localStorage.getItem('tenant') || '',
        },
      });
      const data = response.data.results || response.data;
      const list = Array.isArray(data) ? data : [];
      setBrands(list);
      return list;
    } catch (err) {
      console.error('Error fetching brands:', err);
      setSnackbar({ open: true, message: 'Failed to fetch brands', severity: 'error' });
      return null;
    }
  };

  // Channel Onboarding "Set up RCS" runs the same brand check as the Create Bot button
  useEffect(() => {
    if (props?.pageParams?.startCreateBot) handleCreateBot();
  }, []);

  // A bot needs a brand: with none yet, send the user to Create Brand and continue to the bot form after
  const handleCreateBot = async () => {
    const list = await fetchBrands();
    if (list === null) return;
    if (list.length === 0) {
      setSelectedBrand(null);
      setBrandReturn('bot');
      handleNavigation('create-brand');
    } else {
      setSelectedBrand(null);
      handleNavigation('create-bot');
    }
  };

  const fetchBots = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = {
        'X-Tenant-ID': localStorage.getItem('tenant') || '',
      };
      const response = await axios.get(GET_RCS_BOTS, { headers });
      
      // Ensure bots is always an array
      const botsData = response.data;
      
      if (Array.isArray(botsData)) {
        setBots(botsData);
      } else if (botsData && Array.isArray(botsData.results)) {
        // Handle paginated responses (if your API returns {results: [], count: X})
        setBots(botsData.results);
      } else if (botsData && Array.isArray(botsData.data)) {
        // Handle {data: []} format
        setBots(botsData.data);
      } else if (botsData && typeof botsData === 'object') {
        // Convert object to array if needed
        setBots(Object.values(botsData));
      } else {
        // Default to empty array
        setBots([]);
      }
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch bots');
      console.error('Error fetching bots:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigation = (view, data = null) => {
    setCurrentView(view);
    if (data?.bot) {
      setSelectedBot(data.bot);
      // If we're navigating to bot detail and already have bots loaded,
      // update the selected bot from the list
      if (bots.length > 0 && data.bot.id) {
        const fullBotData = bots.find(b => b.id === data.bot.id);
        if (fullBotData) setSelectedBot(fullBotData);
      }
    }
    if (data?.template) setSelectedTemplate(data.template);
  };

  const handleSuccess = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
    // Refresh data if needed
    if (currentView === 'bot-list') {
      fetchBots();
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get breadcrumb navigation
  const getBreadcrumbs = () => {
    const crumbs = [
      <Link
        key="home"
        underline="hover"
        color="inherit"
        href="#"
        onClick={(e) => {
          e.preventDefault();
          handleNavigation('bot-list');
        }}
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
        RCS Bots
      </Link>,
    ];

    if (currentView === 'bot-detail' && selectedBot) {
      crumbs.push(
        <Typography key="bot-detail" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot.bot_name}
        </Typography>
      );
    } else if (currentView === 'create-bot') {
      crumbs.push(
        <Typography key="create-bot" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Create Bot
        </Typography>
      );
    } else if (currentView === 'edit-bot') {
      crumbs.push(
        <Typography key="edit-bot" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Edit Bot
        </Typography>
      );
    } else if (currentView === 'verify-bot') {
      crumbs.push(
        <Link
          key="bot-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('bot-detail', { bot: selectedBot });
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot?.bot_name}
        </Link>,
        <Typography key="verify-bot" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Verify Bot
        </Typography>
      );
    } else if (currentView === 'template-list' && selectedBot) {
      crumbs.push(
        <Link
          key="bot-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('bot-detail', { bot: selectedBot });
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot.bot_name}
        </Link>,
        <Typography key="templates" color="text.primary" sx={{ display: 'flex', alignItems:'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Templates
        </Typography>
      );
    } else if (currentView === 'create-template') {
      crumbs.push(
        <Link
          key="bot-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('bot-detail', { bot: selectedBot });
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot?.bot_name}
        </Link>,
        <Link
          key="templates-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('template-list');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Templates
        </Link>,
        <Typography key="create-template" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Create Template
        </Typography>
      );
    } else if (currentView === 'edit-template') {
      crumbs.push(
        <Link
          key="bot-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('bot-detail', { bot: selectedBot });
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot?.bot_name}
        </Link>,
        <Link
          key="templates-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('template-list');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Templates
        </Link>,
        <Typography key="edit-template" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Edit Template
        </Typography>
      );
    } else if (currentView === 'broadcast-template') {
      crumbs.push(
        <Link
          key="bot-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('bot-detail', { bot: selectedBot });
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          {selectedBot?.bot_name}
        </Link>,
        <Link
          key="templates-link"
          underline="hover"
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavigation('template-list');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Templates
        </Link>,
        <Typography key="broadcast" color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
          <ChevronRightIcon sx={{ fontSize: 20 }} />
          Broadcast
        </Typography>
      );
    }

    return crumbs;
  };

  // Get current view title
  const getViewTitle = () => {
    const titles = {
      'bot-list': 'RCS Bots',
      'bot-detail': selectedBot?.bot_name || 'Bot Details',
      'create-bot': 'Create New Bot',
      'edit-bot': 'Edit Bot',
      'template-list': 'Templates',
      'create-template': 'Create Template',
      'edit-template': 'Edit Template',
      'broadcast-template': 'Broadcast Template',
      'verify-bot': 'Verify Bot',
    };
    return titles[currentView] || 'RCS Dashboard';
  };

  // Also add a defensive check in BotList.js:
  // In BotList.js, add this check at the beginning:
  const renderView = () => {
    if (loading && currentView === 'bot-list') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading bots...
          </Typography>
        </Box>
      );
    }

    if (error && currentView === 'bot-list') {
      return (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={fetchBots}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }

    // Make sure bots is an array before passing to BotList
    const safeBots = Array.isArray(bots) ? bots : [];

    switch (currentView) {
      case 'bot-list':
        return (
          <BotList
            bots={safeBots}
            onViewBot={(bot) => handleNavigation('bot-detail', { bot })}
            onCreateBot={handleCreateBot}
            onViewBrands={() => handleNavigation('brand-list')}
            onRefresh={fetchBots}
          />
        );

      case 'bot-detail':
        return (
          <BotDetail
            bot={selectedBot}
            onBack={() => handleNavigation('bot-list')}
            onViewTemplates={(bot) => handleNavigation('template-list', { bot })}
            onEditBot={(bot) => handleNavigation('edit-bot', { bot })}
            onSuccess={handleSuccess}
            onCreateTemplate={() => handleNavigation('create-template', { bot: selectedBot })}
            onNavigate={props.onNavigate}
            onVerifyBot={() => handleNavigation('verify-bot', { bot: selectedBot })}
          />
        );

      case 'brand-list':
        if (brands === null) {
          return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          );
        }
        return (
          <BrandList
            brands={brands}
            onBack={() => handleNavigation('bot-list')}
            onCreateBrand={() => {
              setBrandReturn('list');
              handleNavigation('create-brand');
            }}
          />
        );

      case 'create-brand':
        return (
          <CreateBrand
            brands={brands || []}
            deferred={brandReturn === 'bot'}
            initialName={brandReturn === 'bot' ? selectedBrand?.brand_name || '' : ''}
            onBack={() => {
              setSelectedBrand(null);
              handleNavigation(brandReturn === 'bot' ? 'bot-list' : 'brand-list');
            }}
            onCreated={(brand) => {
              if (brandReturn === 'bot') {
                // Not saved yet: the brand is created together with the bot on submit
                setSelectedBrand(brand);
                handleNavigation('create-bot');
              } else {
                handleSuccess('Brand created successfully!');
                handleNavigation('brand-list');
              }
            }}
          />
        );

      case 'create-bot':
      case 'edit-bot':
        return (
          <CreateBot
            bot={currentView === 'edit-bot' ? selectedBot : null}
            lockedBrand={currentView === 'create-bot' ? selectedBrand : null}
            onBack={() => handleNavigation(currentView === 'create-bot' && selectedBrand ? 'create-brand' : 'bot-list')}
            onSuccess={(bot) => {
              handleSuccess(currentView === 'edit-bot' ? 'Bot updated successfully!' : 'Bot created successfully!');
              handleNavigation('bot-detail', { bot });
            }}
            onError={(message) => {
              setSnackbar({
                open: true,
                message,
                severity: 'error',
              });
            }}
          />
        );

      case 'template-list':
        return (
          <TemplateList
            bot={selectedBot}
            onBack={() => handleNavigation('bot-detail', { bot: selectedBot })}
            onCreateTemplate={() => handleNavigation('create-template')}
            onBroadcastTemplate={(template) => handleNavigation('broadcast-template', { template })}
            onEditTemplate={(template) => handleNavigation('edit-template', { template })}
            onSuccess={handleSuccess}
          />
        );

      case 'create-template':
      case 'edit-template':
        return (
          <CreateTemplate
            onNavigate={props.onNavigate}
            bot={selectedBot}
            template={currentView === 'edit-template' ? selectedTemplate : null}
            onBack={() => handleNavigation('bot-list')}
            onSuccess={() => {
              handleSuccess(currentView === 'edit-template' ? 'Template updated successfully!' : 'Template created successfully!');
              handleNavigation('template-list');
            }}
            onError={(message) => {
              setSnackbar({
                open: true,
                message,
                severity: 'error',
              });
            }}
          />
        );

      case 'broadcast-template':
        return (
          <BroadcastTemplate
            template={selectedTemplate}
            bot={selectedBot}
            onBack={() => handleNavigation('bot-list')}
            onSuccess={handleSuccess}
            onError={(message) => {
              setSnackbar({
                open: true,
                message,
                severity: 'error',
              });
            }}
            props={props}
          />
        );

      case 'verify-bot':
        return (
          <BotVerificationWizard
            botId={selectedBot?.id}
            botDotgoId={selectedBot?.dotgo_bot_id}
            onCancel={() => handleNavigation('bot-detail', { bot: selectedBot })}
            onSuccess={() => {
              handleSuccess('Verification submitted successfully!');
              handleNavigation('bot-detail', { bot: selectedBot });
            }}
          />
        );

      default:
        return (
          <BotList
            bots={safeBots}
            onViewBot={(bot) => handleNavigation('bot-detail', { bot })}
            onCreateBot={handleCreateBot}
            onViewBrands={() => handleNavigation('brand-list')}
            onRefresh={fetchBots}
          />
        );
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor:'#FAFAFA' }}>
      <Container maxWidth="xl">
        {/* Header with Breadcrumbs and Actions */}
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
         

          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Show back button for nested views */}
            {/* {!['bot-list', 'create-bot'].includes(currentView) && (
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  if (currentView === 'bot-detail') handleNavigation('bot-list');
                  else if (currentView === 'create-bot') handleNavigation('bot-list');
                  else if (currentView === 'template-list') handleNavigation('bot-detail', { bot: selectedBot });
                  else if (currentView === 'create-template') handleNavigation('template-list');
                  else if (currentView === 'edit-template') handleNavigation('template-list');
                  else if (currentView === 'broadcast-template') handleNavigation('template-list');
                  else if (currentView === 'edit-bot') handleNavigation('bot-detail', { bot: selectedBot });
                }}
                variant="outlined"
              >
                Back
              </Button>
            )} */}

            {/* Show refresh button for list views */}
            {/* {currentView === 'bot-list' && (
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchBots}
                variant="contained"
              >
                Refresh
              </Button>
            )} */}
          </Box>
        </Box>

        {/* Main Content Area */}
        <Paper
          elevation={1}
          sx={{
            p: 3,
            borderRadius: 2,
            backgroundColor: 'background.paper',
            minHeight: 'calc(100vh - 200px)',
          }}
        >
          {renderView()}
        </Paper>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RCSDashboardNew;